/**
 * Global Enterprise Formatting Utility
 * Adheres to Enterprise Application UX Design Contract (Global Standard USD Base).
 * Built entirely on platform Intl APIs.
 */

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_CURRENCY = 'USD';

/**
 * Formats a currency value using Intl.NumberFormat.
 * @param {number|string} value
 * @param {Object} [options]
 * @param {string} [options.currency='USD'] - ISO-4217 code
 * @param {'symbol'|'code'} [options.mode='symbol'] - 'symbol' (narrowSymbol) or 'code' (3-letter)
 * @param {string} [options.locale='en-US']
 * @param {boolean} [options.compact=false] - compact notation (e.g. 1.2M)
 * @param {number} [options.minimumFractionDigits]
 * @param {number} [options.maximumFractionDigits]
 * @returns {string}
 */
export const formatCurrency = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '—';
  }
  const num = Number(value);
  const {
    currency = DEFAULT_CURRENCY,
    mode = 'symbol',
    locale = DEFAULT_LOCALE,
    compact = false,
    minimumFractionDigits,
    maximumFractionDigits,
  } = options;

  const intlOptions = {
    style: 'currency',
    currency,
    currencyDisplay: mode === 'code' ? 'code' : 'narrowSymbol',
  };

  if (compact) {
    intlOptions.notation = 'compact';
    intlOptions.maximumFractionDigits = 1;
  } else {
    if (minimumFractionDigits !== undefined) {
      intlOptions.minimumFractionDigits = minimumFractionDigits;
    }
    if (maximumFractionDigits !== undefined) {
      intlOptions.maximumFractionDigits = maximumFractionDigits;
    }
  }

  try {
    return new Intl.NumberFormat(locale, intlOptions).format(num);
  } catch (e) {
    return `${currency} ${num.toFixed(2)}`;
  }
};

/**
 * Whole currency format (no decimal places, e.g. $1,250)
 */
export const moneyWhole = (value, currency = DEFAULT_CURRENCY, locale = DEFAULT_LOCALE) =>
  formatCurrency(value, {
    currency,
    locale,
    mode: 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

/**
 * Exact currency format (2 decimal places, e.g. $1,250.00)
 */
export const moneyExact = (value, currency = DEFAULT_CURRENCY, locale = DEFAULT_LOCALE) =>
  formatCurrency(value, {
    currency,
    locale,
    mode: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Format a number with locale thousands separators.
 * @param {number|string} value
 * @param {Object} [options]
 * @returns {string}
 */
export const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '—';
  }
  const num = Number(value);
  const {
    locale = DEFAULT_LOCALE,
    compact = false,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  } = options;

  const intlOptions = {
    minimumFractionDigits,
    maximumFractionDigits,
  };

  if (compact) {
    intlOptions.notation = 'compact';
    intlOptions.maximumFractionDigits = 1;
  }

  try {
    return new Intl.NumberFormat(locale, intlOptions).format(num);
  } catch (e) {
    return String(num);
  }
};

/**
 * Short alias for whole number formatting
 */
export const num = (value, locale = DEFAULT_LOCALE) =>
  formatNumber(value, { locale, minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * Formats a percentage value (e.g. 12.5 -> '12.5%')
 * @param {number|string} value
 * @param {Object} [options]
 * @returns {string}
 */
export const formatPercent = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '—';
  }
  const num = Number(value);
  const {
    locale = DEFAULT_LOCALE,
    minimumFractionDigits = 1,
    maximumFractionDigits = 1,
  } = options;

  try {
    // Note: if value is already on a 0-100 scale (e.g. 12.5), we divide by 100 for style: 'percent'
    // or format number with '%' suffix
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num);
    return `${formatted}%`;
  } catch (e) {
    return `${num.toFixed(1)}%`;
  }
};

/**
 * Formats a percentage or value delta with explicit sign and true minus (U+2212).
 * Rule N-4: Explicit sign (+3.2%, −1.8%), true minus glyph, named comparison window.
 * @param {number|string} value
 * @param {Object} [options]
 * @param {string} [options.comparisonPeriod] - e.g. "vs prior month"
 * @param {boolean} [options.isPercentagePoint=false]
 * @returns {{ text: string, sign: '+'|'−'|'', isPositive: boolean, isNegative: boolean, label: string }}
 */
export const formatDelta = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return { text: '—', sign: '', isPositive: false, isNegative: false, label: '' };
  }
  const n = Number(value);
  const { comparisonPeriod = '', isPercentagePoint = false } = options;
  const unit = isPercentagePoint ? 'pp' : '%';
  const absFormatted = Math.abs(n).toFixed(1);

  if (n > 0) {
    return {
      text: `+${absFormatted}${unit}`,
      sign: '+',
      isPositive: true,
      isNegative: false,
      label: comparisonPeriod ? `+${absFormatted}${unit} ${comparisonPeriod}` : `+${absFormatted}${unit}`,
    };
  } else if (n < 0) {
    return {
      text: `\u2212${absFormatted}${unit}`, // True minus U+2212
      sign: '\u2212',
      isPositive: false,
      isNegative: true,
      label: comparisonPeriod ? `\u2212${absFormatted}${unit} ${comparisonPeriod}` : `\u2212${absFormatted}${unit}`,
    };
  }
  return {
    text: `0.0${unit}`,
    sign: '',
    isPositive: false,
    isNegative: false,
    label: comparisonPeriod ? `0.0${unit} ${comparisonPeriod}` : `0.0${unit}`,
  };
};

/**
 * Relative vs Absolute timestamp formatting (Rule N-5).
 * Under 24 hours old: relative ('Just now', '5 min ago', '2 hours ago').
 * Over 24 hours old: absolute ('Sep 3, 2026').
 * Always returns full absolute timestamp with timezone for tooltip/aria.
 * @param {string|Date|number} dateInput
 * @param {Object} [options]
 * @returns {{ display: string, full: string, isRelative: boolean }}
 */
export const formatTimestamp = (dateInput, options = {}) => {
  if (!dateInput) {
    return { display: '—', full: '—', isRelative: false };
  }
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) {
    return { display: String(dateInput), full: String(dateInput), isRelative: false };
  }

  const { locale = DEFAULT_LOCALE } = options;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);

  const fullAbsolute = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(date);

  // If under 24 hours old and not in the future
  if (diffHours < 24 && diffSec >= 0) {
    if (diffMin < 1) {
      return { display: 'Just now', full: fullAbsolute, isRelative: true };
    }
    if (diffMin < 60) {
      return { display: `${diffMin} min ago`, full: fullAbsolute, isRelative: true };
    }
    return { display: `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`, full: fullAbsolute, isRelative: true };
  }

  // Older than 24 hours -> absolute locale-formatted date
  const dateFormatted = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);

  return { display: dateFormatted, full: fullAbsolute, isRelative: false };
};

/**
 * Standard empty-value fallback (Rule N-7).
 * Never returns 'null', 'undefined', 'NaN', 'N/A', or '-'
 * Returns em dash '—' when empty.
 */
export const emptyDash = (val) => {
  if (val === null || val === undefined || val === '' || val === 'N/A' || val === 'null' || val === 'undefined') {
    return '—';
  }
  return val;
};
